import React, { useCallback, useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, AlignmentType, Table, TableRow, TableCell, WidthType, ImageRun, BorderStyle } from "docx";
import axios from "../utils/axiosInstance";
import { useDialog, useToast } from "./ConfirmDialog";
import ImicoSignaturePad from "./ImicoSignaturePad";
import ImicoCertificatePreview from "./ImicoCertificatePreview";
import ImicoNotificationBell from "./ImicoNotificationBell";

const Styles = () => (
  <style>{`
.imico-wrap { font-family: 'Nunito Sans', system-ui, sans-serif; color: #1e293b; }
.imico-wrap * { box-sizing: border-box; }

.imico-topbar { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; flex-wrap:wrap; gap:10px; }
.imico-title { font-size:20px; font-weight:800; color:#1a3a5c; margin:0; }
.imico-subtitle { font-size:12px; color:#64748b; margin:2px 0 0; }

.imico-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:20px; }
.imico-tab { padding:9px 16px; border-radius:8px; border:1px solid #d1d9e6; background:#fff; color:#475569; font-size:13px; font-weight:600; cursor:pointer; }
.imico-tab.active { background:#1a3a5c; color:#fff; border-color:#1a3a5c; }

.imico-card { background:#fff; border:1px solid #e2e8f0; border-radius:10px; padding:20px; margin-bottom:16px; }
.imico-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:14px; }

.imico-field { margin-bottom:14px; }
.imico-field label { display:block; font-size:12px; font-weight:700; color:#475569; margin-bottom:5px; }
.imico-field label .required { color:#dc2626; }
.imico-field input, .imico-field select, .imico-field textarea {
  width:100%; padding:10px 12px; border:1px solid #d1d9e6; border-radius:6px; font-size:14px; background:#f8fafc; font-family:inherit;
}
.imico-field textarea { min-height:140px; resize:vertical; line-height:1.6; }
.imico-field input:focus, .imico-field select:focus, .imico-field textarea:focus { outline:none; border-color:#1a3a5c; background:#fff; }
.imico-hint { font-size:11px; color:#94a3b8; margin-top:4px; }

.imico-btn { display:inline-flex; align-items:center; gap:6px; padding:10px 18px; border:none; border-radius:7px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; }
.imico-btn:disabled { opacity:.5; cursor:not-allowed; }
.imico-btn-primary { background:#1a3a5c; color:#fff; }
.imico-btn-primary:hover:not(:disabled) { background:#0f2a45; }
.imico-btn-success { background:#15803d; color:#fff; }
.imico-btn-warning { background:#d97706; color:#fff; }
.imico-btn-outline { background:transparent; color:#475569; border:1px solid #d1d9e6; }
.imico-btn-outline:hover { background:#f1f5f9; }
.imico-btn-sm { padding:7px 13px; font-size:12px; }
.imico-btn-block { width:100%; justify-content:center; }
.imico-actions-row { display:flex; gap:10px; flex-wrap:wrap; margin-top:16px; }

.imico-alert { padding:10px 14px; border-radius:6px; font-size:12px; margin-bottom:10px; }
.imico-alert-info { background:#eff6ff; border:1px solid #bfdbfe; color:#1e40af; }
.imico-alert-warning { background:#fef3c7; border:1px solid #fde68a; color:#b45309; }
.imico-alert-success { background:#f0fdf4; border:1px solid #bbf7d0; color:#15803d; }

.imico-table-wrap { overflow-x:auto; }
.imico-table { width:100%; border-collapse:collapse; font-size:13px; }
.imico-table th { text-align:left; padding:10px 12px; background:#f1f5f9; color:#475569; font-weight:700; white-space:nowrap; }
.imico-table td { padding:10px 12px; border-top:1px solid #e2e8f0; vertical-align:top; }
.imico-table tr:hover td { background:#f8fafc; cursor:pointer; }
.imico-empty { text-align:center; padding:40px 10px; color:#94a3b8; font-size:13px; }

.imico-badge { display:inline-block; padding:3px 10px; border-radius:14px; font-size:11px; font-weight:700; white-space:nowrap; }
.imico-badge-created { background:#f1f5f9; color:#475569; }
.imico-badge-signed_first { background:#dbeafe; color:#1d4ed8; }
.imico-badge-sent_village { background:#fef3c7; color:#b45309; }
.imico-badge-signed_village { background:#dbeafe; color:#1d4ed8; }
.imico-badge-sent_cell { background:#fef3c7; color:#b45309; }
.imico-badge-received { background:#dcfce7; color:#16a34a; }
.imico-badge-signed_cell { background:#dcfce7; color:#16a34a; }

.imico-workflow { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin:14px 0; }
.imico-workflow-step { font-size:12px; padding:5px 10px; border-radius:6px; background:#f1f5f9; color:#94a3b8; font-weight:600; }
.imico-workflow-step.done { background:#dcfce7; color:#16a34a; }
.imico-workflow-arrow { color:#cbd5e1; }

.imico-bell-wrap { position:relative; }
.imico-bell-btn { position:relative; background:transparent; border:none; cursor:pointer; font-size:20px; color:#475569; padding:8px; border-radius:8px; }
.imico-bell-btn:hover { background:#f1f5f9; }
.imico-bell-badge { position:absolute; top:2px; right:2px; background:#dc2626; color:#fff; font-size:10px; font-weight:700; border-radius:10px; min-width:16px; height:16px; display:flex; align-items:center; justify-content:center; padding:0 4px; }
.imico-bell-backdrop { position:fixed; inset:0; z-index:998; }
.imico-bell-dropdown { position:absolute; right:0; top:calc(100% + 8px); width:min(90vw,340px); background:#fff; border:1px solid #e2e8f0; border-radius:10px; box-shadow:0 10px 30px rgba(0,0,0,.12); z-index:999; overflow:hidden; }
.imico-bell-header { display:flex; justify-content:space-between; align-items:center; padding:12px 14px; border-bottom:1px solid #e2e8f0; font-weight:700; font-size:13px; }
.imico-bell-markall { background:none; border:none; color:#1a3a5c; font-size:11px; font-weight:700; cursor:pointer; }
.imico-bell-list { max-height:320px; overflow-y:auto; }
.imico-bell-item { display:block; width:100%; text-align:left; background:none; border:none; border-bottom:1px solid #f1f5f9; padding:12px 14px; cursor:pointer; }
.imico-bell-item:hover { background:#f8fafc; }
.imico-bell-item.unread { background:#eff6ff; }
.imico-bell-item-title { font-size:12.5px; font-weight:700; color:#1e293b; }
.imico-bell-item-content { font-size:12px; color:#64748b; margin-top:2px; }
.imico-bell-item-time { font-size:10.5px; color:#94a3b8; margin-top:4px; }
.imico-bell-empty { padding:24px; text-align:center; color:#94a3b8; font-size:12.5px; }

/* ── Signature pad ── */
.imico-sig-pad-wrap { margin-top:10px; }
.imico-sig-pad-meta { display:flex; justify-content:space-between; font-size:12px; color:#475569; margin-bottom:6px; }
.imico-sig-pad-label { font-weight:700; }
.imico-sig-pad { background:#f8fafc; border:2px dashed #cbd5e1; border-radius:8px; padding:10px; }
.imico-sig-canvas { width:100% !important; height:160px !important; border-radius:6px; background:#fff; border:1px solid #e2e8f0; touch-action:none; }
.imico-sig-actions { display:flex; gap:8px; justify-content:center; margin-top:8px; }

/* ── Printable certificate paper ── */
.imico-paper { max-width:760px; margin:0 auto; background:#fff; padding:44px 46px; border:1px solid #e2e8f0; border-radius:8px; font-family:'Noto Serif', 'Georgia', serif; color:#1e293b; }
.imico-doc-header { text-align:center; border-bottom:3px double #1a3a5c; padding-bottom:20px; margin-bottom:24px; }
.imico-doc-republic { font-size:16px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:#1a3a5c; margin-bottom:6px; }
.imico-doc-title { font-size:17px; font-weight:700; text-transform:uppercase; margin-top:14px; color:#1a3a5c; }
.imico-doc-number { display:inline-block; margin-top:10px; font-size:11px; font-weight:700; background:#f1f5f9; color:#1a3a5c; padding:4px 12px; border-radius:12px; letter-spacing:0.5px; }
.imico-doc-narrative { font-size:14.5px; line-height:2.1; text-align:justify; margin:22px 0; }
.imico-blank-filled { font-weight:700; border-bottom:1px solid #334155; padding:0 2px; }
.imico-blank-empty { color:#94a3b8; letter-spacing:1px; }
.imico-doc-footer-meta { text-align:right; font-size:12.5px; font-style:italic; color:#475569; margin-bottom:30px; }
.imico-doc-signatures { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; margin-top:20px; }
.imico-doc-sig-block { text-align:center; }
.imico-doc-sig-role { font-size:11.5px; font-weight:700; text-transform:uppercase; color:#64748b; margin-top:4px; }
.imico-doc-sig-line { border-top:1px dotted #94a3b8; width:80%; margin:0 auto 24px; }
.imico-doc-sig-img { max-height:56px; display:block; margin:0 auto 6px; }
.imico-doc-sig-name { font-size:13px; font-weight:700; border-top:1px solid #334155; display:inline-block; padding-top:4px; min-width:140px; }
.imico-doc-sig-date { font-size:11px; color:#94a3b8; margin-top:2px; }

@media print {
  @page { margin:18mm; size:A4; }
  body * { visibility:hidden; }
  #imico-print-area, #imico-print-area * { visibility:visible; }
  #imico-print-area { position:absolute; left:0; top:0; width:100%; border:none; box-shadow:none; }
}

@media (max-width:768px) {
  .imico-doc-signatures { grid-template-columns:1fr; }
  .imico-paper { padding:24px 18px; }
  .imico-tabs { gap:4px; }
  .imico-tab { padding:8px 12px; font-size:12px; }
}
`}</style>
);

const STATUS_LABEL = {
  CREATED: { label: "Yakozwe", cls: "created" },
  SIGNED_BY_FIRST_OFFICIAL: { label: "Yasinywe", cls: "signed_first" },
  SENT_TO_VILLAGE: { label: "Yoherejwe ku Mudugudu", cls: "sent_village" },
  SIGNED_BY_VILLAGE: { label: "Yasinywe n'Umudugudu", cls: "signed_village" },
  SENT_TO_CELL: { label: "Yoherejwe ku Kagari", cls: "sent_cell" },
  RECEIVED_BY_CELL: { label: "Yakiriwe n'Akagari", cls: "received" },
  SIGNED_BY_CELL: { label: "Yasinywe n'Akagari", cls: "signed_cell" },
};

const WORKFLOW_STEPS = [
  "CREATED", "SIGNED_BY_FIRST_OFFICIAL", "SENT_TO_VILLAGE", "SIGNED_BY_VILLAGE", "SENT_TO_CELL", "RECEIVED_BY_CELL", "SIGNED_BY_CELL",
];

function WorkflowTimeline({ status }) {
  const idx = WORKFLOW_STEPS.indexOf(status);
  return (
    <div className="imico-workflow">
      {WORKFLOW_STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <span className={`imico-workflow-step${i <= idx ? " done" : ""}`}>
            {i <= idx ? "✓ " : ""}{STATUS_LABEL[s].label}
          </span>
          {i < WORKFLOW_STEPS.length - 1 && <span className="imico-workflow-arrow">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

const emptyForm = {
  akarere: "", umurenge: "", akagari_id: "", umudugudu_id: "",
  applicant_name: "", applicant_national_id: "", applicant_gender: "", applicant_dob: "", applicant_address: "",
  father_name: "", mother_name: "",
  birth_akarere: "", birth_umurenge: "", birth_akagari: "",
  id_akarere: "", id_umurenge: "",
};

/**
 * Imico n'Imyifatire module. `mode` selects which side of the workflow is
 * rendered: 'creator' (Isibo/Umunyamabanga — create+sign+send), 'village'
 * (Mudugudu — receive+sign+forward) or 'cell' (Akagari — receive/final).
 */
export default function ImicoImyifatire({ mode }) {
  const { toast } = useToast();
  const { confirm } = useDialog();

  const currentUser = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      const payload = token ? JSON.parse(atob(token.split(".")[1])) : {};
      return {
        name: localStorage.getItem("fullname") || payload.fullname || "",
        role: localStorage.getItem("role") || payload.role || "",
      };
    } catch { return { name: "", role: "" }; }
  }, []);

  const [activeTab, setActiveTab] = useState("dashboard");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [akarereOptions, setAkarereOptions] = useState([]);
  const [umurengeOptions, setUmurengeOptions] = useState([]);
  const [akagariOptions, setAkagariOptions] = useState([]);
  const [umuduguduOptions, setUmuduguduOptions] = useState([]);

  const [search, setSearch] = useState({ doc_number: "", applicant_name: "", status: "" });

  const listEndpoint = mode === "village" ? "/api/imico/certificates/inbox/village"
    : mode === "cell" ? "/api/imico/certificates/inbox/cell"
    : "/api/imico/certificates";

  const fetchList = useCallback(async (params) => {
    setLoading(true);
    try {
      const res = await axios.get(listEndpoint, { params: mode === "creator" ? params : undefined });
      setList(res.data || []);
    } catch {
      toast({ type: "danger", title: "Ikosa", message: "Ntibashobotse gupanga urutonde" });
    } finally {
      setLoading(false);
    }
  }, [listEndpoint, mode, toast]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (mode !== "creator" || activeTab !== "create") return;
    axios.get("/api/locations/akarere").then(r => setAkarereOptions(r.data || [])).catch(() => {});
  }, [mode, activeTab]);

  useEffect(() => {
    if (!form.akarere) { setUmurengeOptions([]); return; }
    axios.get("/api/locations/umurenge", { params: { akarere: form.akarere } })
      .then(r => setUmurengeOptions(r.data || [])).catch(() => {});
  }, [form.akarere]);

  useEffect(() => {
    if (!form.akarere || !form.umurenge) { setAkagariOptions([]); return; }
    axios.get("/api/locations/akagari", { params: { akarere: form.akarere, umurenge: form.umurenge } })
      .then(r => setAkagariOptions(r.data || [])).catch(() => {});
  }, [form.akarere, form.umurenge]);

  useEffect(() => {
    if (!form.akagari_id) { setUmuduguduOptions([]); return; }
    axios.get("/api/locations/umudugudu", { params: { akagari_id: form.akagari_id } })
      .then(r => setUmuduguduOptions(r.data || [])).catch(() => {});
  }, [form.akagari_id]);

  const updateForm = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === "akarere") { next.umurenge = ""; next.akagari_id = ""; next.umudugudu_id = ""; }
      if (field === "umurenge") { next.akagari_id = ""; next.umudugudu_id = ""; }
      if (field === "akagari_id") { next.umudugudu_id = ""; }
      return next;
    });
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`/api/imico/certificates/${id}`);
      setDetail(res.data);
      setActiveTab("detail");
    } catch (e) {
      toast({ type: "danger", title: "Ikosa", message: e.response?.data?.error || "Ntibashobotse gufungura icyemezo" });
    }
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!form.akarere || !form.umurenge || !form.akagari_id || !form.umudugudu_id || !form.applicant_name) {
      toast({ type: "warning", title: "Uzuza ibisabwa", message: "Uzuza aho abarizwa n'amakuru y'uwasabye" });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`/api/imico/certificates/${editingId}`, form);
        toast({ type: "success", title: "Byabitswe", message: "Icyemezo cyahinduwe neza" });
      } else {
        const res = await axios.post("/api/imico/certificates", form);
        toast({ type: "success", title: "Byabitswe", message: `Icyemezo cyabitswe (${res.data.doc_number})` });
        setEditingId(res.data.id);
        await loadDetail(res.data.id);
        return;
      }
      setForm(emptyForm);
      setEditingId(null);
      fetchList();
      setActiveTab("drafts");
    } catch (e) {
      toast({ type: "danger", title: "Ikosa", message: e.response?.data?.error || "Ntibyabashije kubikwa" });
    } finally {
      setSaving(false);
    }
  };

  const handleSignFirst = async (dataUrl) => {
    setSaving(true);
    try {
      await axios.post(`/api/imico/certificates/${detail.id}/sign-first`, { signature_image: dataUrl });
      toast({ type: "success", title: "✓ Icyemezo cyasinywe neza", message: "" });
      await loadDetail(detail.id);
      fetchList();
    } catch (e) {
      toast({ type: "danger", title: "Ikosa", message: e.response?.data?.error || "Ntibyashobotse gusinya" });
    } finally {
      setSaving(false);
    }
  };

  const handleSendToVillage = () => {
    confirm({
      type: "info",
      title: "Urifuza koko kohereza iki cyemezo ku Mudugudu?",
      message: `Icyemezo (${detail.doc_number}) kizoherezwa kuri ${detail.umudugudu_name}.`,
      confirmText: "YEGO, OHEREZA",
      cancelText: "OYA",
      onConfirm: async () => {
        try {
          await axios.patch(`/api/imico/certificates/${detail.id}/send-to-village`);
          toast({ type: "success", title: "Byoherejwe", message: "Icyemezo cyoherejwe ku Mudugudu" });
          await loadDetail(detail.id);
          fetchList();
        } catch (e) {
          toast({ type: "danger", title: "Ikosa", message: e.response?.data?.error || "Ntibyashobotse kohereza" });
        }
      },
    });
  };

  const handleSignVillage = async (dataUrl) => {
    setSaving(true);
    try {
      await axios.post(`/api/imico/certificates/${detail.id}/sign-village`, { signature_image: dataUrl });
      toast({ type: "success", title: "✓ Icyemezo cyasinywe nawe neza", message: "" });
      await loadDetail(detail.id);
      fetchList();
    } catch (e) {
      toast({ type: "danger", title: "Ikosa", message: e.response?.data?.error || "Ntibyashobotse gusinya" });
    } finally {
      setSaving(false);
    }
  };

  const handleSendToCell = () => {
    confirm({
      type: "info",
      title: "Urifuza koko kohereza iki cyemezo ku Kagari?",
      message: `Icyemezo (${detail.doc_number}) kizoherezwa kuri ${detail.akagari_name}.`,
      confirmText: "YEGO, OHEREZA",
      cancelText: "OYA",
      onConfirm: async () => {
        try {
          await axios.patch(`/api/imico/certificates/${detail.id}/send-to-cell`);
          toast({ type: "success", title: "Byoherejwe", message: "Icyemezo cyoherejwe ku Kagari" });
          await loadDetail(detail.id);
          fetchList();
        } catch (e) {
          toast({ type: "danger", title: "Ikosa", message: e.response?.data?.error || "Ntibyashobotse kohereza" });
        }
      },
    });
  };

  const handleSignCell = async (dataUrl) => {
    setSaving(true);
    try {
      await axios.post(`/api/imico/certificates/${detail.id}/sign-cell`, { signature_image: dataUrl });
      toast({ type: "success", title: "✓ Icyemezo cyasinywe n'Akagari neza", message: "" });
      await loadDetail(detail.id);
      fetchList();
    } catch (e) {
      toast({ type: "danger", title: "Ikosa", message: e.response?.data?.error || "Ntibyashobotse gusinya" });
    } finally {
      setSaving(false);
    }
  };

  const pdfBlank = (v, n = 14) => (v && String(v).trim()) ? String(v) : "…".repeat(n);
  const pdfShortDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
  };
  const pdfSignerTitle = (role, akagariName) => {
    const r = (role || "").toLowerCase();
    if (r === "isibo") return `Umukuru w'Isibo, mu Kagari ka ${pdfBlank(akagariName, 12)}`;
    return `Umunyamabanga Nshingwabikorwa w'Akagari ka ${pdfBlank(akagariName, 12)}`;
  };

  const handleExportPDF = (cert) => {
    const doc = new jsPDF();
    let y = 18;
    doc.setFont("times", "bold"); doc.setFontSize(14);
    doc.text("REPUBULIKA Y'U RWANDA", 105, y, { align: "center" }); y += 8;
    doc.setFont("times", "bold"); doc.setFontSize(13);
    doc.text("ICYEMEZO GITANGWA N'UBUYOBOZI BW'AKAGARI", 105, y, { align: "center" }); y += 6;
    doc.setFont("times", "normal"); doc.setFontSize(9);
    doc.text(cert.doc_number || "", 105, y, { align: "center" }); y += 5;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(20, y, 190, y);
    doc.setLineDashPattern([], 0);
    y += 10;

    const firstSig = (cert.signatures || []).find(s => s.level === "first");
    const villageSig = (cert.signatures || []).find(s => s.level === "village");
    const cellSig = (cert.signatures || []).find(s => s.level === "cell");
    const signerName = firstSig?.signer_name || cert.created_by_name;
    const signerRole = firstSig?.signer_role || cert.created_by_role;

    const narrative =
      `Njyewe ${pdfBlank(signerName)} ${pdfSignerTitle(signerRole, cert.akagari_name)}, nshingiye ku makuru ` +
      `yatanzwe n'ubuyobozi bw'umudugudu wa ${pdfBlank(cert.umudugudu_name, 14)} ndemeza ko uyu ${pdfBlank(cert.applicant_name, 22)} ` +
      `mwene ${pdfBlank(cert.father_name, 16)} Na ${pdfBlank(cert.mother_name, 16)} wavukiye mu Karere ka ${pdfBlank(cert.birth_akarere, 12)} ` +
      `Umurenge wa ${pdfBlank(cert.birth_umurenge, 12)} /Akagari ka ${pdfBlank(cert.birth_akagari, 12)} wavutse kuwa ${pdfBlank(pdfShortDate(cert.applicant_dob), 10)} ` +
      `Ufite Indangamuntu n° ${pdfBlank(cert.applicant_national_id, 16)} yatangiwe mu Karere ka ${pdfBlank(cert.id_akarere, 12)} ` +
      `Umurenge wa ${pdfBlank(cert.id_umurenge, 12)}. Ni umuturage utuye/ukodesha mu Kagari ka ${pdfBlank(cert.akagari_name, 12)} ` +
      `umudugudu wa ${pdfBlank(cert.umudugudu_name, 12)} akaba ari indakemwa mu mico n'imyifatire.`;

    doc.setFont("times", "normal"); doc.setFontSize(11);
    const narrativeLines = doc.splitTextToSize(narrative, 170);
    doc.text(narrativeLines, 20, y, { align: "justify", maxWidth: 170 });
    y += narrativeLines.length * 6.2 + 8;

    doc.setFont("times", "italic"); doc.setFontSize(10);
    doc.text(`Bikorewe i ${cert.akagari_name || "—"}, kuwa ${new Date(cert.created_at).toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" })}`, 190, y, { align: "right" });
    y += 14;

    if (y > 220) { doc.addPage(); y = 20; }
    doc.setFontSize(9);
    const sigY = y + 26;
    const cols = [{ cx: 43, x1: 20, x2: 66 }, { cx: 105, x1: 82, x2: 128 }, { cx: 167, x1: 144, x2: 190 }];
    doc.setLineDashPattern([1, 1], 0);
    cols.forEach(c => doc.line(c.x1, sigY, c.x2, sigY));
    doc.setLineDashPattern([], 0);
    if (firstSig?.signature_image) { try { doc.addImage(firstSig.signature_image, "PNG", cols[0].x1 + 5, sigY - 16, 36, 14); } catch {} }
    if (villageSig?.signature_image) { try { doc.addImage(villageSig.signature_image, "PNG", cols[1].x1 + 5, sigY - 16, 36, 14); } catch {} }
    if (cellSig?.signature_image) { try { doc.addImage(cellSig.signature_image, "PNG", cols[2].x1 + 5, sigY - 16, 36, 14); } catch {} }
    doc.setFont("times", "bold");
    doc.text(signerName || "-", cols[0].cx, sigY + 5, { align: "center" });
    doc.text(villageSig?.signer_name || "-", cols[1].cx, sigY + 5, { align: "center" });
    doc.text(cellSig?.signer_name || "-", cols[2].cx, sigY + 5, { align: "center" });
    doc.setFont("times", "normal"); doc.setFontSize(7.5);
    doc.text(doc.splitTextToSize(pdfSignerTitle(signerRole, cert.akagari_name), 46), cols[0].cx, sigY + 10, { align: "center" });
    doc.text(doc.splitTextToSize(`Umukuru w'Umudugudu wa ${cert.umudugudu_name || "—"}`, 46), cols[1].cx, sigY + 10, { align: "center" });
    doc.text(doc.splitTextToSize(`Umunyamabanga Nshingwabikorwa w'Akagari ka ${cert.akagari_name || "—"}`, 46), cols[2].cx, sigY + 10, { align: "center" });

    doc.save(`${cert.doc_number || "icyemezo"}.pdf`);
  };

  const dataUrlToUint8Array = (dataUrl) => {
    const base64 = dataUrl.split(",")[1] || "";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  };

  const buildNarrativeRuns = (cert, signerName, signerRole) => {
    const runs = [];
    const text = (t) => runs.push(new TextRun({ text: t }));
    const blank = (v, n = 14) => {
      const has = v && String(v).trim();
      runs.push(new TextRun({ text: has ? String(v) : "…".repeat(n), bold: !!has, underline: has ? { type: "single" } : undefined }));
    };
    text("Njyewe "); blank(signerName);
    text(` ${pdfSignerTitle(signerRole, cert.akagari_name)}, nshingiye ku makuru yatanzwe n'ubuyobozi bw'umudugudu wa `); blank(cert.umudugudu_name, 14);
    text(" ndemeza ko uyu "); blank(cert.applicant_name, 22);
    text(" mwene "); blank(cert.father_name, 16);
    text(" Na "); blank(cert.mother_name, 16);
    text(" wavukiye mu Karere ka "); blank(cert.birth_akarere, 12);
    text(" Umurenge wa "); blank(cert.birth_umurenge, 12);
    text(" /Akagari ka "); blank(cert.birth_akagari, 12);
    text(" wavutse kuwa "); blank(pdfShortDate(cert.applicant_dob), 10);
    text(" Ufite Indangamuntu n° "); blank(cert.applicant_national_id, 16);
    text(" yatangiwe mu Karere ka "); blank(cert.id_akarere, 12);
    text(" Umurenge wa "); blank(cert.id_umurenge, 12);
    text(". Ni umuturage utuye/ukodesha mu Kagari ka "); blank(cert.akagari_name, 12);
    text(" umudugudu wa "); blank(cert.umudugudu_name, 12);
    text(" akaba ari indakemwa mu mico n'imyifatire.");
    return runs;
  };

  const noBorders = {
    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
  };

  const docxSignatureCell = (sig, name, roleText) => new TableCell({
    borders: noBorders,
    width: { size: 33, type: WidthType.PERCENTAGE },
    children: [
      ...(sig?.signature_image
        ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [new ImageRun({ type: "png", data: dataUrlToUint8Array(sig.signature_image), transformation: { width: 100, height: 40 } })] })]
        : [new Paragraph({ text: " " })]),
      new Paragraph({ alignment: AlignmentType.CENTER, border: { top: { style: BorderStyle.SINGLE, size: 4, color: "334155" } }, children: [new TextRun({ text: name || "—", bold: true, size: 20 })] }),
      new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: roleText, size: 16, color: "64748B" })] }),
      ...(sig?.signed_at ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: new Date(sig.signed_at).toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" }), size: 14, color: "94A3B8" })] })] : []),
    ],
  });

  const handleExportDocx = async (cert) => {
    const firstSig = (cert.signatures || []).find(s => s.level === "first");
    const villageSig = (cert.signatures || []).find(s => s.level === "village");
    const cellSig = (cert.signatures || []).find(s => s.level === "cell");
    const signerName = firstSig?.signer_name || cert.created_by_name;
    const signerRole = firstSig?.signer_role || cert.created_by_role;

    const doc = new Document({
      sections: [{
        children: [
          new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 100 }, children: [new TextRun({ text: "REPUBULIKA Y'U RWANDA", bold: true, size: 28 })] }),
          new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 100 }, children: [new TextRun({ text: "ICYEMEZO GITANGWA N'UBUYOBOZI BW'AKAGARI", bold: true, size: 26 })] }),
          new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 300 }, border: { bottom: { style: BorderStyle.DOUBLE, size: 6, color: "1A3A5C" } }, children: [new TextRun({ text: cert.doc_number || "", size: 18 })] }),
          new Paragraph({ spacing: { after: 300, line: 360 }, alignment: AlignmentType.JUSTIFIED, children: buildNarrativeRuns(cert, signerName, signerRole) }),
          new Paragraph({
            alignment: AlignmentType.RIGHT, spacing: { after: 500 },
            children: [new TextRun({ text: `Bikorewe i ${cert.akagari_name || "—"}, kuwa ${new Date(cert.created_at).toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" })}`, italics: true, size: 18 })],
          }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: noBorders,
            rows: [new TableRow({
              children: [
                docxSignatureCell(firstSig, signerName, pdfSignerTitle(signerRole, cert.akagari_name)),
                docxSignatureCell(villageSig, villageSig?.signer_name, `Umukuru w'Umudugudu wa ${cert.umudugudu_name || "—"}`),
                docxSignatureCell(cellSig, cellSig?.signer_name, `Umunyamabanga Nshingwabikorwa w'Akagari ka ${cert.akagari_name || "—"}`),
              ],
            })],
          }),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${cert.doc_number || "icyemezo"}.docx`);
  };

  const filteredList = useMemo(() => {
    let items = list;
    if (search.doc_number) items = items.filter(c => (c.doc_number || "").toLowerCase().includes(search.doc_number.toLowerCase()));
    if (search.applicant_name) items = items.filter(c => (c.applicant_name || "").toLowerCase().includes(search.applicant_name.toLowerCase()));
    if (search.status) items = items.filter(c => c.status === search.status);
    return items;
  }, [list, search]);

  const tabbedList = useMemo(() => {
    if (activeTab === "drafts") return list.filter(c => c.status === "CREATED");
    if (activeTab === "signed") return list.filter(c => ["SIGNED_BY_FIRST_OFFICIAL", "SIGNED_BY_VILLAGE"].includes(c.status));
    if (activeTab === "sent") return list.filter(c => ["SENT_TO_VILLAGE", "SIGNED_BY_VILLAGE", "SENT_TO_CELL", "RECEIVED_BY_CELL", "SIGNED_BY_CELL"].includes(c.status));
    // "received" is the Mudugudu/Akagari inbox tab. The backend inbox
    // endpoints already scope `list` to certificates addressed to this
    // exact Mudugudu/Akagari, at any stage (just arrived, signed, or
    // already confirmed) — do not additionally filter by status here, or
    // newly-arrived certificates (not yet RECEIVED_BY_CELL) never show up.
    if (activeTab === "received") return list;
    return list;
  }, [list, activeTab]);

  const tabs = mode === "creator"
    ? [["dashboard", "Dashboard"], ["create", "Kora Icyemezo"], ["drafts", "Kwandika"], ["signed", "Byasinywe"], ["sent", "Byoherejwe"], ["search", "Shakisha"]]
    : mode === "village"
    ? [["dashboard", "Dashboard"], ["received", "Byakiriwe"], ["search", "Shakisha"]]
    : [["dashboard", "Dashboard"], ["received", "Byakiriwe"], ["search", "Shakisha"]];

  const renderList = (rows) => (
    <div className="imico-table-wrap">
      <table className="imico-table">
        <thead>
          <tr>
            <th>Nomero</th><th>Uwasabye</th><th>Aho abarizwa</th><th>Imiterere</th><th>Itariki</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id} onClick={() => loadDetail(c.id)}>
              <td>{c.doc_number}</td>
              <td>{c.applicant_name}</td>
              <td>{c.umudugudu_name}, {c.akagari_name}</td>
              <td><span className={`imico-badge imico-badge-${STATUS_LABEL[c.status]?.cls}`}>{STATUS_LABEL[c.status]?.label}</span></td>
              <td>{new Date(c.created_at).toLocaleDateString("rw-RW")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <div className="imico-empty">{loading ? "Kuraguza..." : "Nta byemezo bihari"}</div>}
    </div>
  );

  return (
    <div className="imico-wrap">
      <Styles />
      <div className="imico-topbar">
        <div>
          <h2 className="imico-title">Imico n'Imyifatire</h2>
          <p className="imico-subtitle">Icyemezo cy'Imico n'Imyifatire — gukora, gusinya no kohereza</p>
        </div>
        <ImicoNotificationBell onOpenCertificate={loadDetail} />
      </div>

      <div className="imico-tabs">
        {tabs.map(([key, label]) => (
          <button key={key} className={`imico-tab${activeTab === key ? " active" : ""}`} onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "dashboard" && (
        <div className="imico-card">
          <div className="imico-grid">
            <div><div style={{ fontSize: 24, fontWeight: 800, color: "#1a3a5c" }}>{list.length}</div><div className="imico-hint">Byose</div></div>
            <div><div style={{ fontSize: 24, fontWeight: 800, color: "#16a34a" }}>{list.filter(c => c.status === "SIGNED_BY_CELL").length}</div><div className="imico-hint">Byarangiye</div></div>
            <div><div style={{ fontSize: 24, fontWeight: 800, color: "#b45309" }}>{list.filter(c => ["SENT_TO_VILLAGE", "SENT_TO_CELL", "RECEIVED_BY_CELL"].includes(c.status)).length}</div><div className="imico-hint">Bitegereje</div></div>
          </div>
        </div>
      )}

      {activeTab === "create" && mode === "creator" && (
        <form className="imico-card" onSubmit={handleSubmitCreate}>
          <div className="imico-grid">
            <div className="imico-field">
              <label>Hitamo Akarere <span className="required">*</span></label>
              {akarereOptions.length > 0 ? (
                <select value={form.akarere} onChange={e => updateForm("akarere", e.target.value)}>
                  <option value="">-- Hitamo Akarere --</option>
                  {akarereOptions.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input value={form.akarere} onChange={e => updateForm("akarere", e.target.value)} placeholder="Andika Akarere" />
              )}
            </div>
            <div className="imico-field">
              <label>Hitamo Umurenge <span className="required">*</span></label>
              {umurengeOptions.length > 0 ? (
                <select value={form.umurenge} onChange={e => updateForm("umurenge", e.target.value)} disabled={!form.akarere}>
                  <option value="">-- Hitamo Umurenge --</option>
                  {umurengeOptions.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input value={form.umurenge} onChange={e => updateForm("umurenge", e.target.value)} placeholder="Andika Umurenge" disabled={!form.akarere} />
              )}
            </div>
            <div className="imico-field">
              <label>Hitamo Akagari <span className="required">*</span></label>
              <select value={form.akagari_id} onChange={e => updateForm("akagari_id", e.target.value)} disabled={!form.umurenge}>
                <option value="">-- Hitamo Akagari --</option>
                {akagariOptions.map(a => <option key={a.user_id} value={a.user_id}>{a.fullname}</option>)}
              </select>
              {!form.umurenge && <div className="imico-hint">Banza uzuze Akarere n'Umurenge</div>}
              {form.umurenge && akagariOptions.length === 0 && <div className="imico-hint">Kuraguza amakagari...</div>}
            </div>
            <div className="imico-field">
              <label>Hitamo Umudugudu <span className="required">*</span></label>
              <select value={form.umudugudu_id} onChange={e => updateForm("umudugudu_id", e.target.value)} disabled={!form.akagari_id}>
                <option value="">-- Hitamo Umudugudu --</option>
                {umuduguduOptions.map(u => <option key={u.user_id} value={u.user_id}>{u.fullname}</option>)}
              </select>
              {!form.akagari_id && <div className="imico-hint">Banza uhitemo Akagari</div>}
              {form.akagari_id && umuduguduOptions.length === 0 && <div className="imico-hint">Nta mudugudu wanditswe kuri aka Kagari</div>}
            </div>
          </div>

          <div className="imico-grid" style={{ marginTop: 6 }}>
            <div className="imico-field">
              <label>Amazina y'uwasabye <span className="required">*</span></label>
              <input value={form.applicant_name} onChange={e => setForm({ ...form, applicant_name: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Indangamuntu</label>
              <input value={form.applicant_national_id} onChange={e => setForm({ ...form, applicant_national_id: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Igitsina</label>
              <select value={form.applicant_gender} onChange={e => setForm({ ...form, applicant_gender: e.target.value })}>
                <option value="">--</option>
                <option value="Gabo">Gabo</option>
                <option value="Gore">Gore</option>
              </select>
            </div>
            <div className="imico-field">
              <label>Itariki y'amavuko</label>
              <input type="date" value={form.applicant_dob} onChange={e => setForm({ ...form, applicant_dob: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Aho atuye</label>
              <input value={form.applicant_address} onChange={e => setForm({ ...form, applicant_address: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Se (mwene)</label>
              <input value={form.father_name} onChange={e => setForm({ ...form, father_name: e.target.value })} placeholder="Amazina ya se" />
            </div>
            <div className="imico-field">
              <label>Nyina (na)</label>
              <input value={form.mother_name} onChange={e => setForm({ ...form, mother_name: e.target.value })} placeholder="Amazina ya nyina" />
            </div>
          </div>

          <h4 style={{ fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", margin: "18px 0 10px" }}>Aho yavukiye</h4>
          <div className="imico-grid">
            <div className="imico-field">
              <label>Akarere yavukiyemo</label>
              <input value={form.birth_akarere} onChange={e => setForm({ ...form, birth_akarere: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Umurenge yavukiyemo</label>
              <input value={form.birth_umurenge} onChange={e => setForm({ ...form, birth_umurenge: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Akagari yavukiyemo</label>
              <input value={form.birth_akagari} onChange={e => setForm({ ...form, birth_akagari: e.target.value })} />
            </div>
          </div>

          <h4 style={{ fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", margin: "18px 0 10px" }}>Aho yahawe Indangamuntu</h4>
          <div className="imico-grid">
            <div className="imico-field">
              <label>Akarere yahereye Indangamuntu</label>
              <input value={form.id_akarere} onChange={e => setForm({ ...form, id_akarere: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Umurenge yahereye Indangamuntu</label>
              <input value={form.id_umurenge} onChange={e => setForm({ ...form, id_umurenge: e.target.value })} />
            </div>
          </div>

          <div className="imico-actions-row">
            <button type="submit" className="imico-btn imico-btn-primary" disabled={saving}>
              {saving ? "Kubika..." : editingId ? "Hindura" : "Bika (Save Draft)"}
            </button>
          </div>

          <div style={{ marginTop: 28, borderTop: "2px dashed #d1d9e6", paddingTop: 24 }}>
            <h4 style={{ fontSize: 14, fontWeight: 800, color: "#1a3a5c", textTransform: "uppercase", marginBottom: 4 }}>
              Uko icyemezo kizasa — Reba mbere
            </h4>
            <p className="imico-hint" style={{ marginBottom: 16 }}>
              Iyi ni indorerwamo y'icyemezo. Uko wuzuza amakuru haruguru, ni ko azahita agaragara hano.
            </p>
            <ImicoCertificatePreview cert={{
              ...form,
              akagari_name: akagariOptions.find(a => String(a.user_id) === String(form.akagari_id))?.fullname,
              umudugudu_name: umuduguduOptions.find(u => String(u.user_id) === String(form.umudugudu_id))?.fullname,
              created_by_name: currentUser.name,
              created_by_role: currentUser.role,
              created_at: new Date(),
            }} signatures={[]} />
          </div>
        </form>
      )}

      {["dashboard", "drafts", "signed", "sent", "received"].includes(activeTab) && activeTab !== "dashboard" && (
        <div className="imico-card">{renderList(tabbedList)}</div>
      )}
      {activeTab === "dashboard" && <div className="imico-card">{renderList(list.slice(0, 8))}</div>}

      {activeTab === "search" && (
        <div className="imico-card">
          <div className="imico-grid" style={{ marginBottom: 16 }}>
            <div className="imico-field">
              <label>Nomero y'inyandiko</label>
              <input value={search.doc_number} onChange={e => setSearch({ ...search, doc_number: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Amazina y'uwasabye</label>
              <input value={search.applicant_name} onChange={e => setSearch({ ...search, applicant_name: e.target.value })} />
            </div>
            <div className="imico-field">
              <label>Imiterere</label>
              <select value={search.status} onChange={e => setSearch({ ...search, status: e.target.value })}>
                <option value="">Byose</option>
                {Object.keys(STATUS_LABEL).map(k => <option key={k} value={k}>{STATUS_LABEL[k].label}</option>)}
              </select>
            </div>
          </div>
          {renderList(filteredList)}
        </div>
      )}

      {activeTab === "detail" && detail && (
        <div className="imico-card">
          <button className="imico-btn imico-btn-outline imico-btn-sm" onClick={() => setActiveTab("dashboard")} style={{ marginBottom: 16 }}>
            ← Subira inyuma
          </button>
          <WorkflowTimeline status={detail.status} />
          <ImicoCertificatePreview cert={detail} signatures={detail.signatures} />

          <div className="imico-actions-row">
            <button className="imico-btn imico-btn-outline" onClick={() => window.print()}>🖨️ Print</button>
            {mode === "cell" ? (
              <button className="imico-btn imico-btn-outline" onClick={() => handleExportDocx(detail)}>⬇️ Export .docx</button>
            ) : (
              <button className="imico-btn imico-btn-outline" onClick={() => handleExportPDF(detail)}>⬇️ PDF</button>
            )}

            {mode === "creator" && detail.status === "CREATED" && (
              <button className="imico-btn imico-btn-primary" onClick={() => { setForm(detail); setEditingId(detail.id); setActiveTab("create"); }}>
                ✎ Hindura
              </button>
            )}
            {mode === "creator" && detail.status === "SIGNED_BY_FIRST_OFFICIAL" && (
              <button className="imico-btn imico-btn-success" onClick={handleSendToVillage}>OHEREZA KU MUDUGUDU</button>
            )}
            {mode === "village" && detail.status === "SIGNED_BY_VILLAGE" && (
              <button className="imico-btn imico-btn-success" onClick={handleSendToCell}>OHEREZA KU KAGARI</button>
            )}
          </div>

          {mode === "creator" && detail.status === "CREATED" && (
            <div className="imico-card" style={{ marginTop: 16 }}>
              <ImicoSignaturePad label="SINYA" signerName={detail.created_by_name} signerRole="Isibo/Umunyamabanga" onSign={handleSignFirst} saving={saving} />
            </div>
          )}
          {mode === "village" && detail.status === "SENT_TO_VILLAGE" && (
            <div className="imico-card" style={{ marginTop: 16 }}>
              <ImicoSignaturePad label="SINYA NAWE" signerName={detail.umudugudu_name} signerRole="Umukuru w'Umudugudu" onSign={handleSignVillage} saving={saving} />
            </div>
          )}
          {mode === "cell" && ["SENT_TO_CELL", "RECEIVED_BY_CELL"].includes(detail.status) && (
            <div className="imico-card" style={{ marginTop: 16 }}>
              <ImicoSignaturePad label="SINYA" signerName={detail.akagari_name} signerRole="Umunyamabanga Nshingwabikorwa w'Akagari" onSign={handleSignCell} saving={saving} />
            </div>
          )}

          {detail.audit_trail?.length > 0 && (
            <div className="imico-card" style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 10 }}>Amateka y'igikorwa (Audit Trail)</h4>
              <div className="imico-table-wrap">
                <table className="imico-table">
                  <thead><tr><th>Igikorwa</th><th>Uwakoze</th><th>Uruhare</th><th>Itariki</th></tr></thead>
                  <tbody>
                    {detail.audit_trail.map(a => (
                      <tr key={a.id}><td>{a.action}</td><td>{a.user_name || "—"}</td><td>{a.role || "—"}</td><td>{new Date(a.created_at).toLocaleString("rw-RW")}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
